import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { BELogo } from "../components/BELogo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0e0e10] text-white flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="absolute top-6 left-6">
        <Link to="/" className="inline-flex items-center gap-3">
          <BELogo size={32} />
          <span className="text-xs tracking-[0.2em] uppercase text-white/60">Bhumi Equipments</span>
        </Link>
      </div>

      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#141417] border border-white/10 p-10 rounded-3xl"
        data-testid="admin-login-form"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#FF6A13]/15 text-[#FF6A13] mb-6">
          <Lock size={22} />
        </div>
        <h1 className="h2-section text-3xl">Dealer Portal</h1>
        <p className="text-sm text-white/60 mt-2">Sign in to manage demo bookings.</p>

        <div className="mt-8 space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-white/50">Email</Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              className="mt-2 h-12 rounded-xl bg-[#0e0e10] border-white/10 text-white focus-visible:ring-[#FF6A13]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-white/50">Password</Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              className="mt-2 h-12 rounded-xl bg-[#0e0e10] border-white/10 text-white focus-visible:ring-[#FF6A13]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {err && (
            <div className="text-sm text-red-400" data-testid="login-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#FF6A13] hover:bg-[#E85A0D] text-white font-semibold"
            data-testid="login-submit"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </div>
      </form>
    </div>
  );
}
